import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { clearPreviewSession } from '../lib/previewSession';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [locked, setLocked] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Defensive: if someone reached /login while a stale portal-preview session
  // is still in localStorage, clear it — otherwise the axios interceptor would
  // attach X-Preview-As-User-Id and the read-only middleware would block login.
  useEffect(() => {
    clearPreviewSession();
    // iter303 — Offboarding: show a clear notice when redirected here after being
    // archived/deactivated mid-session (interceptor appends ?disabled=1).
    try {
      if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('disabled') === '1') {
        setError('Your account has been deactivated. Please contact an administrator.');
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (submitted && user && user.role) {
      const adminRoles = ['admin', 'super_admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'];
      const techRoles = ['employee', 'technician'];
      if (adminRoles.includes(user.role)) {
        navigate('/admin/dashboard');
      } else if (techRoles.includes(user.role)) {
        navigate('/employee/timeclock');
      } else if (user.role === 'customer') {
        navigate('/customer/dashboard');
      } else if (user.role === 'vip') {
        navigate('/vip/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [submitted, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      setSubmitted(true);
    } else if (result.error === 'EMAIL_NOT_VERIFIED') {
      // Trigger a resend so they get a fresh code (preview mode also returns the code)
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await resp.json();
        navigate('/verify', { state: { email, previewCode: data.verification_code || null, deliveryMode: data.delivery_mode } });
      } catch {
        navigate('/verify', { state: { email } });
      }
    } else if (result.error === 'PENDING_APPROVAL') {
      setError("Your account is verified but awaiting admin approval. You'll be notified by email once approved.");
    } else if (result.error === 'ACCOUNT_LOCKED') {
      setLocked(true);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
      {locked && (
        <div data-testid="login-locked-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6">
          <div className="w-full max-w-sm bg-[#141414] border border-[#EF4444]/40 rounded-lg p-6 text-center space-y-4">
            <p className="text-white text-lg font-semibold">Error logging in</p>
            <p className="text-[#A3A3A3] text-sm">Please contact NexFlow directly.</p>
            <button
              data-testid="login-locked-dismiss"
              onClick={() => { setLocked(false); setPassword(''); }}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] h-10 rounded font-medium hover:bg-[#F3E5AB]"
            >
              Close
            </button>
          </div>
        </div>
      )}
      <div className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="lg" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mt-4 mb-2">LOCAL. TRUSTED. RELIABLE.</p>
          <p className="text-[#A3A3A3]">Sign in to your account</p>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div data-testid="login-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">
                {typeof error === 'string' ? error : (() => { try { return JSON.stringify(error); } catch { return 'Login failed'; } })()}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#141414] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                data-testid="login-password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#141414] border-[#262626] text-white focus:ring-1 focus:ring-[#D4AF37]"
              />
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-medium transition-all duration-200"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A3A3A3] text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#D4AF37] hover:text-[#F3E5AB]">
                Register
              </Link>
            </p>
            <div className="mt-4 flex justify-center gap-x-4 text-[10px] text-[#525252]">
              <Link to="/sms-consent" data-testid="login-sms-opt-in-link" className="hover:text-[#A3A3A3]">SMS Consent</Link>
              <Link to="/terms" className="hover:text-[#A3A3A3]">Terms</Link>
              <Link to="/privacy" className="hover:text-[#A3A3A3]">Privacy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
